import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router-dom'
import { withStyles } from '@material-ui/core'
import { connect } from 'react-redux'
import Heading from '../../../../uiKit/texts/Heading'
import Switch from '../../../../uiKit/Switch'
import Select from '../../../../uiKit/Select/Select'
import Modal from '../../../../uiKit/Modal'
import AIFeaturesToggle from './AIFeaturesToggle'
import AIFeaturesConfirmModal from './AIFeaturesConfirmModal'
import ChangeAIModelConfirmModal from '../emailAutomation/ChangeAIModelConfirmModal'
import ChangeQCSConfirmModal from '../emailAutomation/ChangeQCSConfirmModal'
import {
  getAIFeedbackStatus,
  updateAIFeedbackStatus,
  getAIModels,
  getPipelineFlags,
  updatePipelineFlags,
} from '../../api/settings'
import LoaderSmall from '../../../../uiKit/loaders/loaderSmall'


const styles = theme => ({
  paragraph: {
    fontFamily: 'Lato',
    fontSize: 14,
    color: 'var(--color-text-secondary)',
  },
  container: {
    maxWidth: 927,
    minHeight: 750,
  },
  toggleContainer: {
    marginTop: 30,
    marginBottom: 30,
    overflow: 'visible',
  },
  featureCard: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px 16px 20px',
    backgroundColor: '#fff',
    borderRadius: '10px',
    marginTop: '16px',
    boxShadow: '0px 0px 5.2px 2px #86868626',
    border: '1px solid #EBEBEB',
    overflow: 'visible',
  },
  aiModelFeatureCard: {
    position: 'relative',
    zIndex: 2,
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  textContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: '16px',
    maxWidth: '705px',
  },
  title: {
    fontWeight: '700',
    fontSize: '16px',
    marginBottom: '4px',
    color: '#333333',
  },
  description: {
    fontSize: '14px',
    color: '#7a7a7a',
  },
  aiModelSelectWrap: {
    width: 240,
    flexShrink: 0,
    position: 'relative',
    zIndex: 2,
  },
  customModalPaper: {
    padding: '0px!important',
    height: '246px!important',
    width: '540px!important',
  },
  aiModelModalPaper: {
    padding: '0!important',
    width: '540px!important',
  },
})

const AIFeaturesManagement = ({ classes, activeBot }) => {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [aiFeedbackEnabled, setAiFeedbackEnabled] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [pendingToggleState, setPendingToggleState] = useState(false)

  const [aiModels, setAiModels] = useState([])
  const [selectedAiModel, setSelectedAiModel] = useState(null)
  const [pendingAiModel, setPendingAiModel] = useState(null)
  const [showAiModelModal, setShowAiModelModal] = useState(false)
  const [qcsEnabled, setQcsEnabled] = useState(false)
  const [pendingQcs, setPendingQcs] = useState(false)
  const [showQcsModal, setShowQcsModal] = useState(false)

  useEffect(() => {
    let cancelled = false

    getAIModels()
      .then(models => {
        if (cancelled) return
        setAiModels(Array.isArray(models) ? models : [])
      })
      .catch(() => null)

    return () => {
      cancelled = true
    }
  }, [])

  const loadPipelineFlags = botId => {
    return getPipelineFlags(botId)
      .then(flags => {
        if (!flags) return
        const aiModel = flags.aiModel
        setSelectedAiModel(
          aiModel ? { label: aiModel.modelName, value: aiModel.id } : null,
        )
        setQcsEnabled(Boolean(flags.qcsEnabled))
      })
  }

  useEffect(() => {
    if (!activeBot || !activeBot.id) {
      setLoading(false)
      setSelectedAiModel(null)
      setQcsEnabled(false)
      return
    }

    setLoading(true)

    Promise.all([
      getAIFeedbackStatus(activeBot.id),
      getPipelineFlags(activeBot.id),
    ])
      .then(([feedbackResponse, flags]) => {
        if (feedbackResponse !== null && feedbackResponse !== undefined) {
          setAiFeedbackEnabled(
            feedbackResponse.aiFeedbackEnabled !== undefined ? feedbackResponse.aiFeedbackEnabled :
              feedbackResponse.enabled !== undefined ? feedbackResponse.enabled :
                feedbackResponse.isAiFeedbackEnabled !== undefined ? feedbackResponse.isAiFeedbackEnabled :
                  typeof feedbackResponse === 'boolean' ? feedbackResponse : false
          )
        } else {
          setAiFeedbackEnabled(false)
        }

        if (flags) {
          const aiModel = flags.aiModel
          setSelectedAiModel(
            aiModel ? { label: aiModel.modelName, value: aiModel.id } : null,
          )
          setQcsEnabled(Boolean(flags.qcsEnabled))
        } else {
          setSelectedAiModel(null)
          setQcsEnabled(false)
        }
      })
      .catch(error => {
        console.error('Error fetching AI features settings:', error)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [activeBot])

  const handleToggle = () => {
    setShowConfirmModal(true)
    setPendingToggleState(!aiFeedbackEnabled)
  }

  const handleConfirmToggle = () => {
    setShowConfirmModal(false)
    setSaving(true)

    updateAIFeedbackStatus(activeBot.id, pendingToggleState)
      .then(() => {
        setAiFeedbackEnabled(pendingToggleState)
      })
      .catch(error => {
        console.error('Error updating AI feedback status:', error)
        setPendingToggleState(!pendingToggleState)
      })
      .finally(() => {
        setSaving(false)
      })
  }

  const handleCancelToggle = () => {
    setShowConfirmModal(false)
    setPendingToggleState(aiFeedbackEnabled)
  }

  const handleAiModelChange = option => {
    if (!option || (selectedAiModel && option.value === selectedAiModel.value)) {
      return
    }
    setPendingAiModel(option)
    setShowAiModelModal(true)
  }

  const handleCancelAiModelChange = () => {
    setShowAiModelModal(false)
    setPendingAiModel(null)
  }

  const handleConfirmAiModelChange = () => {
    if (!activeBot || !activeBot.id || !pendingAiModel) return

    const nextModel = pendingAiModel
    setShowAiModelModal(false)
    setSaving(true)

    updatePipelineFlags(activeBot.id, {
      aiModelId: nextModel.value,
      qcsEnabled,
    })
      .then(() => loadPipelineFlags(activeBot.id))
      .catch(() => null)
      .finally(() => {
        setPendingAiModel(null)
        setSaving(false)
      })
  }

  const handleQcsToggle = () => {
    setPendingQcs(!qcsEnabled)
    setShowQcsModal(true)
  }

  const handleCancelQcsChange = () => {
    setShowQcsModal(false)
    setPendingQcs(qcsEnabled)
  }

  const handleConfirmQcsChange = () => {
    if (!activeBot || !activeBot.id) return

    const nextQcs = pendingQcs
    setShowQcsModal(false)
    setSaving(true)

    updatePipelineFlags(activeBot.id, {
      aiModelId: selectedAiModel ? selectedAiModel.value : null,
      qcsEnabled: nextQcs,
    })
      .then(() => loadPipelineFlags(activeBot.id))
      .catch(() => null)
      .finally(() => {
        setSaving(false)
      })
  }

  return (
    <div>
      <div className={classes.container}>
        <Heading>AI Features Management</Heading>
        <p className={classes.paragraph}>
                    Manage and customise AI-powered tools available in your chat assistant.
        </p>

        <div className={classes.toggleContainer}>
          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 100 }}>
              <LoaderSmall showLoader />
            </div>
          ) : (
            <React.Fragment>
              <AIFeaturesToggle enabled={aiFeedbackEnabled} onChange={handleToggle} />

              <div className={`${classes.featureCard} ${classes.aiModelFeatureCard}`}>
                <div className={classes.iconContainer}>
                  <img src="/images/platform/efficiency.svg" alt="AI model icon" />
                  <div className={classes.textContainer}>
                    <div className={classes.title}>AI Model Selection</div>
                    <div className={classes.description}>
                      Select between available AI models to customize how your assistant generates responses.
                    </div>
                  </div>
                </div>
                <div className={classes.aiModelSelectWrap}>
                  <Select
                    placeholder="Select AI model"
                    options={aiModels.map(model => ({
                      label: model.modelName,
                      value: model.id,
                    }))}
                    value={selectedAiModel}
                    onChange={handleAiModelChange}
                    isSearchable={false}
                  />
                </div>
              </div>

              <div className={classes.featureCard}>
                <div className={classes.iconContainer}>
                  <img src="/images/platform/scs.svg" alt="QCS icon" />
                  <div className={classes.textContainer}>
                    <div className={classes.title}>
                      {qcsEnabled
                        ? 'Disable Quality Compliance Systems (QCS)'
                        : 'Enable Quality Compliance Systems (QCS)'}
                    </div>
                    <div className={classes.description}>
                      {!qcsEnabled
                        ? 'Enable QCS to provide responses aligned with quality standards, compliance requirements, and best practices.'
                        : 'Disable QCS to stop responses aligned with quality standards, compliance requirements, and best practices.'}
                    </div>
                  </div>
                </div>
                <Switch checked={qcsEnabled} onChange={handleQcsToggle} />
              </div>

              {saving && (
                <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
                  <LoaderSmall showLoader />
                </div>
              )}
            </React.Fragment>
          )}
        </div>

        <Modal
          open={showConfirmModal}
          onClose={handleCancelToggle}
          disableClose={false}
          classes={{ paper: classes.customModalPaper }}>
          <AIFeaturesConfirmModal
            isEnabling={pendingToggleState}
            onConfirm={handleConfirmToggle}
            onCancel={handleCancelToggle}
          />
        </Modal>

        <Modal
          open={showAiModelModal}
          onClose={handleCancelAiModelChange}
          classes={{ paper: classes.aiModelModalPaper }}>
          <ChangeAIModelConfirmModal
            modelName={pendingAiModel && pendingAiModel.label}
            onConfirm={handleConfirmAiModelChange}
            onCancel={handleCancelAiModelChange}
          />
        </Modal>

        <Modal
          open={showQcsModal}
          onClose={handleCancelQcsChange}
          classes={{ paper: classes.aiModelModalPaper }}>
          <ChangeQCSConfirmModal
            isEnabling={pendingQcs}
            onConfirm={handleConfirmQcsChange}
            onCancel={handleCancelQcsChange}
          />
        </Modal>
      </div>
    </div>
  )
}

AIFeaturesManagement.propTypes = {
  classes: PropTypes.object.isRequired,
  activeBot: PropTypes.object.isRequired,
}

const mapStateToProps = state => ({
  activeBot: state.activeBot,
})

export default withRouter(withStyles(styles, { withTheme: true })(connect(mapStateToProps)(AIFeaturesManagement)))
